import React, { useCallback, useEffect, useRef, useState, cloneElement, Children } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';

const RESIZE_HANDLE_HEIGHT = 6;

const ResizableTableRow = ({
  children,
  rowId,
  height,
  minHeight = 40,
  maxHeight = 200,
  onHeightChange,
  onKeyDown,  // F2 키 지원을 위해 추가
  tabIndex,   // 키보드 포커스 지원을 위해 추가
  ...tableRowProps
}) => {
  const { sx: tableRowSx, ...restTableRowProps } = tableRowProps || {};
  const [isResizing, setIsResizing] = useState(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(height);
  const rowRef = useRef(null);

  useEffect(() => {
    startHeightRef.current = height;
  }, [height]);

  const handleMouseMove = useCallback(
    (event) => {
      if (!isResizing) return;
      const delta = event.clientY - startYRef.current;
      let nextHeight = startHeightRef.current + delta;
      nextHeight = Math.max(minHeight, Math.min(maxHeight, nextHeight));
      if (nextHeight !== startHeightRef.current) {
        onHeightChange(nextHeight, rowId);
      }
    },
    [isResizing, maxHeight, minHeight, onHeightChange, rowId]
  );

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
    }
  }, [isResizing]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleMouseDown = (event) => {
    setIsResizing(true);
    startYRef.current = event.clientY;
    startHeightRef.current = height;
    event.preventDefault();
    event.stopPropagation();
  };

  // 마지막 자식에 리사이즈 핸들 추가
  const childrenArray = Children.toArray(children);
  const lastChild = childrenArray[childrenArray.length - 1];
  
  // 마지막 자식이 TableCell인 경우에만 리사이즈 핸들 추가
  const enhancedLastChild = lastChild && React.isValidElement(lastChild) && lastChild.type === TableCell
    ? cloneElement(lastChild, {
        ...lastChild.props,
        sx: {
          ...lastChild.props.sx,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -RESIZE_HANDLE_HEIGHT / 2,
            left: 0,
            right: 0,
            height: RESIZE_HANDLE_HEIGHT,
            cursor: 'row-resize',
            zIndex: 10,
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s',
          },
          '&:hover::after': {
            backgroundColor: 'rgba(123, 104, 238, 0.2)',
          },
        },
        onMouseDown: (e) => {
          // 마지막 셀의 하단 가장자리에서만 리사이즈 시작
          const rect = e.currentTarget.getBoundingClientRect();
          const y = e.clientY - rect.top;
          if (y >= rect.height - RESIZE_HANDLE_HEIGHT) {
            handleMouseDown(e);
          }
          if (lastChild.props.onMouseDown) {
            lastChild.props.onMouseDown(e);
          }
        },
      })
    : lastChild;

  return (
    <TableRow
      ref={rowRef}
      tabIndex={tabIndex}  // 키보드 포커스 지원
      onKeyDown={onKeyDown}  // F2 키 이벤트 전달
      {...restTableRowProps}
      sx={(theme) => ({
        cursor: isResizing ? 'row-resize' : 'default',
        userSelect: isResizing ? 'none' : 'auto',
        height,
        minHeight: height,
        maxHeight: height,
        position: 'relative',
        ...(typeof tableRowSx === 'function' ? tableRowSx(theme) : tableRowSx)
      })}
    >
      {childrenArray.slice(0, -1)}
      {enhancedLastChild}
    </TableRow>
  );
};

export default ResizableTableRow;

